import {
    IAfterGuiAttachedParams,
    IDoesFilterPassParams,
    IFilterComp,
    IFilterParams,
    IFloatingFilterComp,
    IFloatingFilterParams,
} from '@ag-grid-community/core';

export interface BranchOption {
    id: string;
    name: string;
}

export interface BranchFilterParams extends IFilterParams {
    branches: BranchOption[];
}

export class BranchFilter implements IFilterComp {
    filterParams!: BranchFilterParams;
    filterValue!: string | null;
    gui!: HTMLDivElement;
    eFilterSelect!: HTMLSelectElement;

    init(params: BranchFilterParams) {
        this.filterParams = params;
        this.filterValue = null;
        this.setupGui(params);
    }

    setupGui(params: BranchFilterParams) {
        this.gui = document.createElement('div');
        this.gui.className = 'ag-filter-body-wrapper';

        const container = document.createElement('div');
        container.className = 'ag-filter-body';
        container.style.padding = '8px';

        this.eFilterSelect = document.createElement('select');
        this.eFilterSelect.style.width = '100%';
        this.eFilterSelect.style.height = '28px';
        this.eFilterSelect.className = 'ag-input-field-input ag-text-field-input';

        // Add "All" option
        const allOption = document.createElement('option');
        allOption.value = '';
        allOption.textContent = 'All Branches';
        this.eFilterSelect.appendChild(allOption);

        // Add branch options
        params.branches.forEach((branch) => {
            const option = document.createElement('option');
            option.value = branch.id;
            option.textContent = branch.name;
            this.eFilterSelect.appendChild(option);
        });

        this.eFilterSelect.addEventListener('change', () => {
            this.filterValue = this.eFilterSelect.value || null;
            params.filterChangedCallback();
        });

        container.appendChild(this.eFilterSelect);
        this.gui.appendChild(container);
    }

    getGui() {
        return this.gui;
    }

    doesFilterPass(params: IDoesFilterPassParams) {
        const { node } = params;
        const branchName = this.filterParams.getValue(node)?.toString();

        if (!this.filterValue) {
            return true;
        }

        // Find the branch name for the selected ID
        const selectedBranch = this.filterParams.branches.find((b) => b.id === this.filterValue);
        if (!selectedBranch) {
            return true;
        }

        return branchName === selectedBranch.name;
    }

    isFilterActive() {
        return this.filterValue != null && this.filterValue !== '';
    }

    getModel() {
        if (!this.isFilterActive()) {
            return null;
        }

        return { value: this.filterValue };
    }

    setModel(model: { value: string } | null) {
        const newValue = model == null ? null : model.value;
        this.eFilterSelect.value = newValue || '';
        this.filterValue = newValue;
    }

    afterGuiAttached(params?: IAfterGuiAttachedParams): void {
        if (!params?.suppressFocus) {
            this.eFilterSelect.focus();
        }
    }
}

export class BranchFloatingFilter implements IFloatingFilterComp {
    private eGui!: HTMLDivElement;
    private eSelect!: HTMLSelectElement;
    private params!: IFloatingFilterParams<BranchFilter>;

    init(params: IFloatingFilterParams<BranchFilter>) {
        this.params = params;
        this.eGui = document.createElement('div');
        this.eGui.style.width = '100%';
        this.eGui.style.height = '100%';
        this.eGui.style.display = 'flex';
        this.eGui.style.alignItems = 'center';

        this.eSelect = document.createElement('select');
        this.eSelect.style.width = '100%';
        this.eSelect.style.height = '24px';
        this.eSelect.style.fontSize = '12px';
        this.eSelect.className = 'ag-input-field-input ag-text-field-input';

        // Options will be populated when parentFilterInstance is available
        this.params.parentFilterInstance((instance) => {
            const branches = instance.filterParams.branches;

            // Add "All" option
            const allOption = document.createElement('option');
            allOption.value = '';
            allOption.textContent = 'All';
            this.eSelect.appendChild(allOption);

            // Add branch options
            branches.forEach((branch) => {
                const option = document.createElement('option');
                option.value = branch.id;
                option.textContent = branch.name;
                this.eSelect.appendChild(option);
            });
        });

        this.eSelect.addEventListener('change', () => {
            this.params.parentFilterInstance((instance) => {
                instance.setModel(this.eSelect.value ? { value: this.eSelect.value } : null);
                instance.filterParams.filterChangedCallback();
            });
        });

        this.eGui.appendChild(this.eSelect);
    }

    getGui() {
        return this.eGui;
    }

    onParentModelChanged(parentModel: { value: string } | null) {
        this.eSelect.value = parentModel?.value || '';
    }

    destroy() {
        // Cleanup if needed
    }
}
